import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Coins, ShieldAlert, RefreshCw, Trash2, X, TrendingUp } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";
import { LineChart, Line, YAxis, ResponsiveContainer, Tooltip } from "recharts";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const ExchangeModal = ({ open, onOpenChange }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [conn, setConn] = useState(null); // {connected, env}
  // connect form
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [env, setEnv] = useState("live");
  const [provider, setProvider] = useState("binance");
  const [connecting, setConnecting] = useState(false);
  // data
  const [balances, setBalances] = useState([]);
  const [orders, setOrders] = useState([]);
  const [chart, setChart] = useState([]);
  // order form
  const [symbol, setSymbol] = useState("BTCUSDT");
  const [side, setSide] = useState("BUY");
  const [otype, setOtype] = useState("MARKET");
  const [qty, setQty] = useState("");
  const [price, setPrice] = useState("");
  const [preview, setPreview] = useState(null);
  const [placing, setPlacing] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/exchange/status`);
      setConn(r.data);
    } catch (e) { setConn({ connected: false }); }
    finally { setLoading(false); }
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [b, o] = await Promise.all([
        axios.get(`${API}/exchange/balances`),
        axios.get(`${API}/exchange/open-orders`),
      ]);
      setBalances(b.data.balances || []);
      setOrders(o.data.orders || []);
    } catch (e) { /* surfaced elsewhere */ }
  }, []);

  const loadChart = useCallback(async (sym) => {
    try {
      const r = await axios.get(`${API}/exchange/klines`, { params: { symbol: sym, interval: "1h", limit: 72 } });
      setChart((r.data.klines || []).map((k) => ({ t: k.t, c: k.c })));
    } catch (e) { setChart([]); }
  }, []);

  useEffect(() => { if (open) loadStatus(); }, [open, loadStatus]);
  useEffect(() => {
    if (open && conn?.connected) { loadData(); loadChart(symbol); }
  }, [open, conn, loadData, loadChart]); // eslint-disable-line react-hooks/exhaustive-deps

  const doConnect = async () => {
    if (!apiKey.trim() || !apiSecret.trim()) { toast.error(t("exchange_keys_required")); return; }
    setConnecting(true);
    try {
      const r = await axios.post(`${API}/exchange/connect`, { provider, api_key: apiKey, api_secret: apiSecret, env });
      toast.success(t("exchange_connected"));
      setApiKey(""); setApiSecret("");
      setConn(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("exchange_connect_failed"));
    } finally { setConnecting(false); }
  };

  const doDisconnect = async () => {
    try { await axios.delete(`${API}/exchange/disconnect`); setConn({ connected: false }); setBalances([]); setOrders([]); toast.success(t("exchange_disconnected")); }
    catch (e) { toast.error(t("exchange_connect_failed")); }
  };

  const doPreview = async () => {
    if (!qty || Number(qty) <= 0) { toast.error(t("exchange_qty_required")); return; }
    if (otype === "LIMIT" && (!price || Number(price) <= 0)) { toast.error(t("exchange_price_required")); return; }
    try {
      const r = await axios.post(`${API}/exchange/order/preview`, {
        symbol, side, type: otype, quantity: qty, price: otype === "LIMIT" ? price : undefined,
      });
      setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || t("exchange_order_failed")); }
  };

  const doConfirm = async () => {
    setPlacing(true);
    try {
      await axios.post(`${API}/exchange/order/confirm`, { confirmation_token: preview.confirmation_token });
      toast.success(t("exchange_order_placed"));
      setPreview(null); setQty(""); setPrice("");
      loadData();
    } catch (e) { toast.error(e?.response?.data?.detail || t("exchange_order_failed")); }
    finally { setPlacing(false); }
  };

  const doCancel = async (o) => {
    try { await axios.delete(`${API}/exchange/order`, { data: { symbol: o.symbol, order_id: String(o.orderId) } }); toast.success(t("exchange_order_cancelled")); loadData(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("exchange_order_failed")); }
  };

  const isLive = conn?.env === "live";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto bg-[var(--bg-secondary)] border-[var(--border-color)]" data-testid="exchange-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[var(--text-primary)]">
            <Coins size={20} className="text-amber-400" /> {t("exchange_title")}
            {conn?.connected && (
              <>
                <span className="text-xs capitalize text-[var(--text-secondary)]">· {conn.provider}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${isLive ? "bg-red-500/20 text-red-300" : "bg-emerald-500/20 text-emerald-300"}`}>
                  {isLive ? "LIVE" : "TESTNET"}
                </span>
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-amber-400" /></div>
        ) : !conn?.connected ? (
          /* ── Connect form ── */
          <div className="space-y-3" data-testid="exchange-connect-form">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs">
              <ShieldAlert size={16} className="shrink-0 mt-0.5" />
              <span>{t("exchange_connect_warning")}</span>
            </div>
            <div className="flex gap-2">
              {["binance", "bybit"].map((pv) => (
                <button key={pv} onClick={() => setProvider(pv)} data-testid={`exchange-provider-${pv}`}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold border capitalize transition-all ${provider === pv ? "bg-amber-500/20 border-amber-500 text-amber-200" : "border-[var(--border-color)] text-[var(--text-muted)]"}`}>
                  {pv}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              {["live", "testnet"].map((e) => (
                <button key={e} onClick={() => setEnv(e)} data-testid={`exchange-env-${e}`}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all ${env === e ? (e === "live" ? "bg-red-500/20 border-red-500 text-red-200" : "bg-emerald-500/20 border-emerald-500 text-emerald-200") : "border-[var(--border-color)] text-[var(--text-muted)]"}`}>
                  {e === "live" ? t("exchange_env_live") : t("exchange_env_testnet")}
                </button>
              ))}
            </div>
            <Input value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="API Key" data-testid="exchange-api-key"
              className="bg-[var(--input-bg)] border-[var(--border-color)] text-[var(--text-primary)]" />
            <Input value={apiSecret} onChange={(e) => setApiSecret(e.target.value)} type="password" placeholder="API Secret" data-testid="exchange-api-secret"
              className="bg-[var(--input-bg)] border-[var(--border-color)] text-[var(--text-primary)]" />
            <p className="text-[11px] text-[var(--text-muted)]">{t("exchange_keys_hint")}</p>
            <Button onClick={doConnect} disabled={connecting} className="w-full bg-amber-600 hover:bg-amber-500 text-white" data-testid="exchange-connect-btn">
              {connecting ? <Loader2 size={16} className="animate-spin mr-2" /> : <Coins size={16} className="mr-2" />}
              {t("exchange_connect_btn")}
            </Button>
          </div>
        ) : (
          /* ── Connected dashboard ── */
          <div className="space-y-4">
            {/* Chart */}
            <div className="rounded-lg border border-[var(--border-color)] p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-[var(--text-secondary)] flex items-center gap-1"><TrendingUp size={14} className="text-amber-400" /> {symbol} · 1h</span>
                <button onClick={() => { loadData(); loadChart(symbol); }} data-testid="exchange-refresh" className="text-[var(--text-muted)] hover:text-amber-400"><RefreshCw size={14} /></button>
              </div>
              <div style={{ width: "100%", height: 120 }}>
                <ResponsiveContainer>
                  <LineChart data={chart}>
                    <YAxis domain={["auto", "auto"]} hide />
                    <Tooltip contentStyle={{ background: "#1a1a1a", border: "1px solid #333", fontSize: 11 }} labelFormatter={() => ""} formatter={(v) => [v, "Fiyat"]} />
                    <Line type="monotone" dataKey="c" stroke="#f59e0b" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Balances */}
            <div>
              <div className="text-xs font-semibold text-[var(--text-secondary)] mb-1.5">{t("exchange_balances")}</div>
              {balances.length === 0 ? (
                <p className="text-xs text-[var(--text-muted)]">{t("exchange_no_balance")}</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5" data-testid="exchange-balances">
                  {balances.map((b) => (
                    <div key={b.asset} className="flex justify-between items-center px-2.5 py-1.5 rounded-lg bg-[var(--bg-tertiary)] text-xs">
                      <span className="font-medium text-[var(--text-primary)]">{b.asset}</span>
                      <span className="text-[var(--text-secondary)]">{parseFloat(b.free).toLocaleString(undefined, { maximumFractionDigits: 6 })}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Order form */}
            <div className="rounded-lg border border-[var(--border-color)] p-3 space-y-2" data-testid="exchange-order-form">
              <div className="text-xs font-semibold text-[var(--text-secondary)]">{t("exchange_new_order")}</div>
              <div className="flex gap-2">
                <Input value={symbol} onChange={(e) => setSymbol(e.target.value.toUpperCase())} onBlur={() => loadChart(symbol)} placeholder="BTCUSDT" data-testid="exchange-symbol"
                  className="bg-[var(--input-bg)] border-[var(--border-color)] text-[var(--text-primary)] flex-1" />
                <div className="flex rounded-lg overflow-hidden border border-[var(--border-color)]">
                  {["BUY", "SELL"].map((s) => (
                    <button key={s} onClick={() => setSide(s)} data-testid={`exchange-side-${s}`}
                      className={`px-3 text-xs font-semibold ${side === s ? (s === "BUY" ? "bg-emerald-600 text-white" : "bg-red-600 text-white") : "text-[var(--text-muted)]"}`}>
                      {s === "BUY" ? t("exchange_buy") : t("exchange_sell")}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <div className="flex rounded-lg overflow-hidden border border-[var(--border-color)]">
                  {["MARKET", "LIMIT"].map((ty) => (
                    <button key={ty} onClick={() => setOtype(ty)} data-testid={`exchange-type-${ty}`}
                      className={`px-3 py-2 text-xs font-medium ${otype === ty ? "bg-amber-600 text-white" : "text-[var(--text-muted)]"}`}>{ty}</button>
                  ))}
                </div>
                <Input value={qty} onChange={(e) => setQty(e.target.value)} placeholder={t("exchange_quantity")} data-testid="exchange-qty"
                  className="bg-[var(--input-bg)] border-[var(--border-color)] text-[var(--text-primary)] flex-1" />
                {otype === "LIMIT" && (
                  <Input value={price} onChange={(e) => setPrice(e.target.value)} placeholder={t("exchange_price")} data-testid="exchange-price"
                    className="bg-[var(--input-bg)] border-[var(--border-color)] text-[var(--text-primary)] flex-1" />
                )}
              </div>
              <Button onClick={doPreview} className="w-full bg-amber-600 hover:bg-amber-500 text-white" data-testid="exchange-preview-btn">
                {t("exchange_review_order")}
              </Button>
            </div>

            {/* Open orders */}
            <div>
              <div className="text-xs font-semibold text-[var(--text-secondary)] mb-1.5">{t("exchange_open_orders")}</div>
              {orders.length === 0 ? (
                <p className="text-xs text-[var(--text-muted)]">{t("exchange_no_orders")}</p>
              ) : (
                <div className="space-y-1.5" data-testid="exchange-open-orders">
                  {orders.map((o) => (
                    <div key={o.orderId} className="flex justify-between items-center px-2.5 py-1.5 rounded-lg bg-[var(--bg-tertiary)] text-xs">
                      <span className="text-[var(--text-primary)]">
                        <b className={o.side === "BUY" ? "text-emerald-400" : "text-red-400"}>{o.side}</b> {o.symbol} · {o.origQty} @ {o.price}
                      </span>
                      <button onClick={() => doCancel(o)} data-testid={`exchange-cancel-${o.orderId}`} className="text-red-400 hover:text-red-300"><X size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button onClick={doDisconnect} data-testid="exchange-disconnect-btn" className="text-xs text-[var(--text-muted)] hover:text-red-400 flex items-center gap-1">
              <Trash2 size={12} /> {t("exchange_disconnect")}
            </button>
          </div>
        )}

        {/* Order confirmation */}
        {preview && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" data-testid="exchange-confirm-overlay">
            <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-xl p-5 max-w-sm w-full space-y-3">
              <h3 className="font-semibold text-[var(--text-primary)] flex items-center gap-2"><ShieldAlert size={18} className="text-amber-400" /> {t("exchange_confirm_title")}</h3>
              <p className={`text-sm font-bold ${preview.env === "live" ? "text-red-400" : "text-emerald-400"}`}>
                {preview.env === "live" ? "LIVE" : "TESTNET"}: {preview.summary.side} {preview.summary.quantity} {preview.summary.symbol} {preview.summary.type}{preview.summary.price ? ` @ ${preview.summary.price}` : ""}
              </p>
              <p className="text-xs text-[var(--text-muted)]">{t("exchange_confirm_desc")}</p>
              <div className="flex gap-2">
                <Button onClick={() => setPreview(null)} variant="outline" className="flex-1 border-[var(--border-color)]" data-testid="exchange-confirm-cancel">{t("cancel")}</Button>
                <Button onClick={doConfirm} disabled={placing} className="flex-1 bg-red-600 hover:bg-red-500 text-white" data-testid="exchange-confirm-place">
                  {placing ? <Loader2 size={16} className="animate-spin mr-1" /> : null}{t("exchange_confirm_place")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ExchangeModal;
